import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { Button, Tooltip } from "@smart/itops-sb-design-system-dom";
import { useUpdateSetting } from "@smart/manage-gql-client-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { AreasToAffect } from "./areas-to-affect";
import { BackgroundColor } from "./background-color";
import { BrandColor } from "./brand-color";
import { BrandLogo } from "./brand-logo";
import { ButtonCornerShape } from "./branding-corner-shape";
import { HeaderDisplay } from "./header-display";
import { HeaderTextColor } from "./header-text-color";
import { InputFieldShape } from "./input-field-shape";
import { BrandingInput, getDefaultValues } from "../types";

const Divider = () => <div className="border-b border-neutral-100" />;

type BrandingSettingsContentProps = {
  onSave: (values: BrandingInput) => Promise<void>;
  onReset: () => void;
  saving: boolean;
};

const BrandingSettingsContent = ({
  onSave,
  onReset,
  saving,
}: BrandingSettingsContentProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const { handleSubmit } = useFormContext<BrandingInput>();
  const [logoUploading, setLogoUploading] = useState(false);

  const onSubmit = handleSubmit(async (data) => {
    await onSave(data);
    publishEvent?.({
      category: "track",
      name: "IntakeBrandingAdded",
      screen: "Branding",
      entryPoint: "IntakeSettings",
      component: "SaveButton",
      jiraKey: "LINK-3255",
      interactionType: "Active",
    }).catch(console.error);
  });

  const isDisabled = saving || logoUploading;

  return (
    <div className="flex flex-col gap-large px-[1.6rem] py-[2.4rem]">
      <div className="flex flex-col gap-large">
        <BrandColor />
        <AreasToAffect disabled={isDisabled} />
        <Divider />
        <HeaderTextColor disabled={isDisabled} />
        <Divider />
        <BackgroundColor disabled={isDisabled} />
        <Divider />
        <HeaderDisplay disabled={isDisabled} />
        <Divider />
        <ButtonCornerShape disabled={isDisabled} />
        <Divider />
        <InputFieldShape disabled={isDisabled} />
        <Divider />
        <BrandLogo uploading={logoUploading} setUploading={setLogoUploading} />
      </div>
      <Divider />
      <div className="flex gap-small">
        <Button
          text="Save"
          variant="primaryDefault"
          size="small"
          onClick={onSubmit}
          loading={saving}
          disabled={isDisabled}
        />
        <Tooltip
          text="Resetting will clear your custom branding and restore the default appearance."
          placement="top-end"
          offsetBy={10}
        >
          <Button
            text="Reset"
            variant="primarySubtle"
            size="small"
            disabled={isDisabled}
            onClick={onReset}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export const BrandingSettings = () => {
  const [update, { loading: saving }] = useUpdateSetting();
  const { reset } = useFormContext<BrandingInput>();

  const handleSave = async (values: BrandingInput) => {
    try {
      await update({
        variables: {
          fields: {
            branding: values,
          },
        },
      });
    } catch (error) {
      console.error("Failed to save branding settings:", error);
      throw error;
    }
  };

  const handleReset = async () => {
    try {
      await update({
        variables: {
          fields: {
            branding: {},
          },
        },
      });
      reset(getDefaultValues());
    } catch (error) {
      console.error("Failed to reset branding settings:", error);
      throw error;
    }
  };

  return (
    <BrandingSettingsContent
      onSave={handleSave}
      onReset={handleReset}
      saving={saving}
    />
  );
};
